import { useState, useEffect, useMemo } from "react";

import { useQuery, useQueryClient, useMutation } from "react-query";
import { Auth } from "shared/@auth/Auth";
import { useLocation } from "react-router-dom";

export function useProjectsQueries() {
  const { makeAuthedRequest } = Auth.useContainer();
  const location = useLocation();
  function pathParam(location) {
    return location === "/management-team/projects" ? "mt" : "ec";
  }
  const [_location, setLocation] = useState(pathParam(location.pathname));

  useEffect(() => {
    setLocation(pathParam(location.pathname));
  }, [location.pathname]);

  // CURRENT PROJECTS
  const {
    data: projectData,
    refetch: currentRefetch,
    status: currentStatus,
  } = useQuery(
    ["projects", _location],
    async () =>
      await makeAuthedRequest(
        `/management-team/projects/${_location}/current`
      ).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      })
  );

  const { projects } = useMemo(() => {
    if (projectData == null) {
      return { projects: [] };
    } else {
      return { projects: projectData };
    }
  }, [projectData]);

  // ARCHIVED PROJECTS
  const {
    data: archiveProjectData,
    refetch: archiveRefetch,
    status: archiveStatus,
  } = useQuery(
    ["archiveProjects", _location],
    async () =>
      await makeAuthedRequest(
        `/management-team/projects/${_location}/archived`
      ).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      })
  );

  const { archiveProjects } = useMemo(() => {
    if (archiveProjectData == null) {
      return { archiveProjects: [] };
    } else {
      return { archiveProjects: archiveProjectData };
    }
  }, [archiveProjectData]);

  const fetchFile = (fileName) =>
    makeAuthedRequest(`/management-team/downloadfile/projects/${fileName}`, {
      method: "GET",
    })
      .then((response) => {
        response.blob().then((blob) => {
          const blobObject = new Blob([blob], { type: blob.type });
          const url = URL.createObjectURL(blobObject);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
        });
      })
      .catch((err) => {
        console.log(err);
      });

  return {
    projects,
    archiveProjects,
    fetchFile,
    currentRefetch,
    archiveRefetch,
    currentStatus,
    archiveStatus,
    _location,
  };
}

export function useProjectsMutations() {
  const { makeAuthedRequest } = Auth.useContainer();
  const queryClient = useQueryClient();
  const location = useLocation();
  function pathParam(location) {
    return location === "/management-team/projects" ? "mt" : "ec";
  }
  const [_location, setLocation] = useState(pathParam(location.pathname));

  useEffect(() => {
    setLocation(pathParam(location.pathname));
  }, [location.pathname]);

  // SAVE NOTE
  const notePost = useMutation(
    ["notes"],
    async (noteObject) =>
      makeAuthedRequest(`/management-team/project/note/add`, {
        method: "POST",
        body: JSON.stringify(noteObject),
      }).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () => {
        queryClient.invalidateQueries("documents");
        queryClient.invalidateQueries(["projects", _location]);
        queryClient.invalidateQueries(["archiveProjects", _location]);
      },
    }
  );

  // EDIT NOTE
  const notePut = useMutation(
    ["notes"],
    async (noteObject) =>
      makeAuthedRequest(`/management-team/project/note/update`, {
        method: "PUT",
        body: JSON.stringify(noteObject),
      }).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () => {
        queryClient.invalidateQueries("documents");
        queryClient.invalidateQueries(["projects", _location]);
        queryClient.invalidateQueries(["archiveProjects", _location]);
      },
    }
  );

  // DELETE NOTE
  const noteDelete = useMutation(
    ["notes"],
    async (noteObject) =>
      makeAuthedRequest(`/management-team/project/note/delete`, {
        method: "DELETE",
        body: JSON.stringify(noteObject),
      }).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () => {
        queryClient.invalidateQueries("documents");
        queryClient.invalidateQueries(["projects", _location]);
        queryClient.invalidateQueries(["archiveProjects", _location]);
      },
    }
  );

  // Status Change
  const statusPut = useMutation(
    ["status"],
    async (statusObject) =>
      makeAuthedRequest(`/management-team/project/status/update`, {
        method: "PUT",
        body: JSON.stringify(statusObject),
      }).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () => {
        queryClient.invalidateQueries("documents");
        queryClient.invalidateQueries(["projects", _location]);
        queryClient.invalidateQueries(["archiveProjects", _location]);
      },
    }
  );

  return {
    notePost,
    notePut,
    noteDelete,
    statusPut,
  };
}

// Combined hook for backward compatibility
export function useProjects() {
  const queries = useProjectsQueries();
  const mutations = useProjectsMutations();
  return {
    ...queries,
    ...mutations,
  };
}
